import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AWS_CONFIG } from '../config/aws';
import { VEHICLE_TYPES } from '../data/services';
import { Icon } from './icons';
import { AddServiceModal, EditServiceModal } from './ServiceModals';

export default function PricingManager() {
  const { getIdToken } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editPrices, setEditPrices] = useState({});
  const [saving, setSaving] = useState(null);
  const [saved, setSaved] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingService, setEditingService] = useState(null);

  const fetchServices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${AWS_CONFIG.apiUrl}/services`);
      const data = await res.json();
      const items = data.services || [];
      setServices(items);
      const prices = {};
      for (const svc of items) {
        prices[svc.serviceId] = { ...svc.prices };
      }
      setEditPrices(prices);
    } catch (err) {
      console.error('Failed to fetch services:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const handlePriceChange = (serviceId, vehicleType, value) => {
    const numValue = parseInt(value, 10) || 0;
    setEditPrices(prev => ({
      ...prev,
      [serviceId]: { ...prev[serviceId], [vehicleType]: numValue },
    }));
  };

  const handleDelete = async (svc) => {
    const confirmText = window.prompt(
      `"${svc.name}" 서비스를 정말 삭제할까요?\n\n` +
      `기존 예약 기록에는 영향 없지만 신규 예약에서 사라집니다.\n` +
      `확인하려면 서비스 이름을 정확히 입력하세요: "${svc.name}"`
    );
    if (confirmText !== svc.name) {
      if (confirmText !== null) alert('이름이 일치하지 않아 취소되었습니다.');
      return;
    }
    setDeleting(svc.serviceId);
    try {
      const token = await getIdToken();
      const res = await fetch(`${AWS_CONFIG.apiUrl}/services/${svc.serviceId}`, {
        method: 'DELETE',
        headers: { Authorization: token },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        alert(`삭제 실패: ${body.error || res.status}`);
      } else {
        await fetchServices();
      }
    } catch (err) {
      alert(`서버 오류: ${err.message}`);
    }
    setDeleting(null);
  };

  const handleSave = async (serviceId) => {
    setSaving(serviceId);
    setSaved(null);
    try {
      const token = await getIdToken();
      const res = await fetch(`${AWS_CONFIG.apiUrl}/services/${serviceId}/prices`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: token },
        body: JSON.stringify({ prices: editPrices[serviceId] }),
      });
      if (res.ok) {
        setSaved(serviceId);
        setTimeout(() => setSaved(null), 2000);
      } else {
        alert('저장에 실패했습니다.');
      }
    } catch {
      alert('서버 연결에 실패했습니다.');
    }
    setSaving(null);
  };

  if (loading) {
    return <div><h1>가격 관리</h1><p style={{ color: 'var(--text-muted)' }}>로딩 중...</p></div>;
  }

  return (
    <div>
      <h1>가격 관리</h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <button className="btn btn-primary" onClick={() => setShowAddModal(true)}
          style={{ fontSize: '0.85rem', padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Icon name="Plus" size={16} /> 서비스 추가
        </button>
      </div>

      {showAddModal && (
        <AddServiceModal
          onClose={() => setShowAddModal(false)}
          onCreated={() => { setShowAddModal(false); fetchServices(); }}
          existingIds={services.map(s => s.serviceId)}
          getIdToken={getIdToken}
        />
      )}

      {editingService && (
        <EditServiceModal
          service={editingService}
          onClose={() => setEditingService(null)}
          onSaved={() => { setEditingService(null); fetchServices(); }}
          getIdToken={getIdToken}
        />
      )}

      {services.map(svc => (
        <div key={svc.serviceId} className="card" style={{ padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{svc.icon}</span> {svc.name}
              <span style={{ color: 'var(--text-dim)', fontSize: '0.75rem', fontWeight: 400 }}>
                ({svc.serviceId})
              </span>
              <button className="btn btn-outline"
                onClick={() => setEditingService(svc)}
                style={{ padding: '4px 10px', fontSize: '0.75rem', marginLeft: 4 }}
                title="이름·설명·아이콘 수정">
                ✏️ 편집
              </button>
            </h3>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary"
                disabled={saving === svc.serviceId || deleting === svc.serviceId}
                onClick={() => handleSave(svc.serviceId)}
                style={{ padding: '6px 20px', fontSize: '0.85rem' }}>
                {saving === svc.serviceId ? '저장 중...'
                  : saved === svc.serviceId ? '저장됨 ✓'
                  : '저장'}
              </button>
              <button className="btn btn-outline"
                disabled={saving === svc.serviceId || deleting === svc.serviceId}
                onClick={() => handleDelete(svc)}
                style={{ padding: '6px 14px', fontSize: '0.85rem', color: '#ff5252', borderColor: '#ff5252' }}>
                {deleting === svc.serviceId ? '삭제 중...' : '삭제'}
              </button>
            </div>
          </div>
          <table style={{
            width: '100%', borderCollapse: 'collapse',
            background: 'var(--surface)', borderRadius: 'var(--radius)',
          }}>
            <thead>
              <tr style={{ background: 'var(--surface-elev)' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: '0.85rem', color: 'var(--text-muted)' }}>차종</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', fontSize: '0.85rem', color: 'var(--text-muted)' }}>가격 (원)</th>
              </tr>
            </thead>
            <tbody>
              {VEHICLE_TYPES.map(v => (
                <tr key={v.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 16px', fontSize: '0.9rem' }}>
                    {v.name} <span style={{ color: 'var(--text-dim)', fontSize: '0.8rem' }}>({v.example})</span>
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                    <input type="number" step="1000" min="0"
                      value={editPrices[svc.serviceId]?.[v.id] || 0}
                      onChange={e => handlePriceChange(svc.serviceId, v.id, e.target.value)}
                      style={{
                        width: 120, padding: '6px 10px', borderRadius: 'var(--radius)',
                        border: '1px solid var(--border)', background: 'var(--bg)',
                        color: 'var(--text)', textAlign: 'right', fontSize: '0.9rem',
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}
